import { apiRequest } from "./client";
import type {
  GeneratedFormDraft,
  GeneratedFormFieldDraft,
  GeneratedFormSectionDraft,
  GenerateFormFieldsRequest,
} from "../types/generateFields";

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isFieldArray(value: unknown): value is GeneratedFormFieldDraft[] {
  return Array.isArray(value) && value.every(isRecord);
}

export function normalizeGeneratedFormResponse(
  body: unknown,
): GeneratedFormDraft {
  const data = isRecord(body) && "data" in body ? body.data : body;
  if (!isRecord(data)) {
    throw new Error("Некорректный ответ генерации полей");
  }

  const fields = isFieldArray(data.fields) ? data.fields : null;
  let sections: GeneratedFormSectionDraft[] | undefined;
  if (Array.isArray(data.sections)) {
    if (
      !data.sections.every(
        (section) => isRecord(section) && isFieldArray(section.fields),
      )
    ) {
      throw new Error("Некорректный ответ генерации полей");
    }
    sections = data.sections.map((section, index) => ({
      label: String(section.label ?? "").trim() || `Раздел ${index + 1}`,
      order: typeof section.order === "number" ? section.order : index,
      fields: section.fields as GeneratedFormFieldDraft[],
    }));
  }

  if (!fields && !sections?.length) {
    throw new Error("Некорректный ответ генерации полей");
  }

  return {
    name: String(data.name ?? "").trim() || "Новая форма",
    description:
      data.description == null ? null : String(data.description).trim() || null,
    fields: fields ?? [],
    ...(sections ? { sections } : {}),
    meta: isRecord(data.meta) ? data.meta : undefined,
  };
}

/** POST /api/v1/forms/generate-fields — черновик полей из текста (DeepSeek). */
export async function generateFormFields(
  payload: GenerateFormFieldsRequest,
): Promise<GeneratedFormDraft> {
  const body = await apiRequest<unknown>("/api/v1/forms/generate-fields", {
    method: "POST",
    body: JSON.stringify({
      prompt: payload.prompt.trim(),
      ...(payload.name?.trim() ? { name: payload.name.trim() } : {}),
    }),
  });

  return normalizeGeneratedFormResponse(body);
}
